import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Button,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Snackbar,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useRecoilValue } from "recoil";
import axios from "axios";

import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";
import { designTokens } from "../theme";

// =========================================
// 型定義
// =========================================

// 一覧表示用
type SalesHistoryRow = {
  company_id: number;
  company_name: string;
  contact_person: string;
  sales_person: string;
  sales_date: string;
  sales_approach_method: string;
  purchase_intention: string;
  proposed_service: string;
  proposal_details: string;
  man_hours: string;
  sales_review: string;
  next_action_id: number;
  sales_status_id: number;
  sales_status: string;
  lost_reason_id?: number | null;
  lost_reason_name?: string | null;
  recycle_base_priority?: string | null;
};

// フォーム用
type SalesHistoryForm = {
  company_id: number | "";
  company_name: string;
  contact_person: string;
  sales_person_id: number | "";
  sales_date: string;
  sales_approach_method_id: number | "";
  purchase_intention_id: number | "";
  proposed_service_id: number | "";
  proposal_details: string;
  man_hours: string;
  sales_review: string;
  next_action_id: number | "";
  sales_status_id: number | "";
  lost_reason_id: number | "";
  lead_sources_id: number | "";
  referrer: string;
  customer_needs: string;
  competitor_information: string;
  follow_up_deadline: string;
  request_inquiry: string;
  issue_complaint: string;
  next_response_details: string;
  response_deadline: string;
};

// マスタ共通
type MasterItem = {
  id: number;
  name: string;
  recycle_base_priority?: string;
};

type ProjectMasterItem = MasterItem & {
  company_id: number;
  customer_type_id: 1 | 2;
  status: "active" | "closed";
  project_start_reason?: string;
};

type Masters = {
  company: MasterItem[];
  project: ProjectMasterItem[];
  sales_person: MasterItem[];
  sales_approach_method: MasterItem[];
  purchase_intention: MasterItem[];
  proposed_service: MasterItem[];
  next_action: MasterItem[];
  sales_status: MasterItem[];
  lost_reason: MasterItem[];
  lead_sources: MasterItem[];
};

type SnackbarSeverity = "success" | "error" | "warning" | "info";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
};

const ATTACHMENT_ACCEPT = [".pdf"].join(",");
const ATTACHMENT_MAX_SIZE = 20 * 1024 * 1024;

const isSupportedAttachment = (file: File) => {
  const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
  return extension === ".pdf";
};

const escapeIcsText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");

const toIcsDate = (value: string) => value.replace(/-/g, "");

const toNextIcsDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  const nextDate = new Date(Date.UTC(year, month - 1, day + 1));
  return nextDate.toISOString().slice(0, 10).replace(/-/g, "");
};

// =========================================
// 初期値
// =========================================

const emptyFormData: SalesHistoryForm = {
  company_id: "",
  company_name: "",
  contact_person: "",
  sales_person_id: "",
  sales_date: "",
  sales_approach_method_id: "",
  purchase_intention_id: "",
  proposed_service_id: "",
  proposal_details: "",
  man_hours: "",
  sales_review: "",
  next_action_id: "",
  sales_status_id: "",
  lost_reason_id: "",
  lead_sources_id: "",
  referrer: "",
  customer_needs: "",
  competitor_information: "",
  follow_up_deadline: "",
  request_inquiry: "",
  issue_complaint: "",
  next_response_details: "",
  response_deadline: "",
};

const emptyMasters: Masters = {
  company: [],
  project: [],
  sales_person: [],
  sales_approach_method: [],
  purchase_intention: [],
  proposed_service: [],
  next_action: [],
  sales_status: [],
  lost_reason: [],
  lead_sources: [],
};

// =========================================
// マスタ整形
// PostgreSQLから返る id が文字列になる場合があるため数値化
// =========================================

const normalizeMasterList = (
  list: any[] | undefined, // 引数：配列またはundefined
  idKeys: string[] = ["id"], // IDとして利用するキー名の配列
  nameKeys: string[] = ["name"], // 名前として利用するキー名の配列
): MasterItem[] => {
  // 戻り値：MasterItem型の配列
  return (list ?? []).map((item) => {
    // listがnull/undefinedなら空配列を使用し、各要素をMasterItem形式に変換

    const idValue = idKeys
      .map((key) => item?.[key]) // idKeysに定義されたキーの値を取得
      .find((value) => value != null); // null/undefinedではない最初の値を取得

    const nameValue = nameKeys
      .map((key) => item?.[key]) // nameKeysに定義されたキーの値を取得
      .find((value) => value != null); // null/undefinedではない最初の値を取得

    return {
      // MasterItem型のオブジェクトを生成
      id: Number(idValue ?? 0), // idがなければ0、数値型へ変換
      name: String(nameValue ?? ""), // nameがなければ空文字、文字列型へ変換

      recycle_base_priority:
        item?.recycle_base_priority ?? // camelCase版を取得
        item?.Recycle_base_priority ?? // APIによっては先頭大文字の場合もある
        "", // なければ空文字
    };
  });
};

const normalizeMasters = (source: any): Masters => {
  return {
    company: normalizeMasterList(
      source?.company,
      ["id", "company_id"],
      ["name", "company_name"],
    ),
    project: (
      source?.project ??
      source?.projects ??
      source?.project_list ??
      []
    ).map((item: any) => ({
      id: Number(item.id ?? item.project_id),
      name: String(item.name ?? item.project_name ?? ""),
      company_id: Number(item.company_id),
      customer_type_id: Number(item.customer_type_id) === 2 ? 2 : 1,
      status: item.status === "closed" ? "closed" : "active",
      project_start_reason: String(item.project_start_reason ?? ""),
    })),
    sales_person: normalizeMasterList(
      source?.sales_person,
      ["id", "sales_person_id"],
      ["name", "sales_person"],
    ),
    sales_approach_method: normalizeMasterList(
      source?.sales_approach_method,
      ["id", "sales_approach_method_id"],
      ["name", "sales_approach_method"],
    ),
    purchase_intention: normalizeMasterList(
      source?.purchase_intention,
      ["id", "purchase_intention_id"],
      ["name", "purchase_intention"],
    ),
    proposed_service: normalizeMasterList(
      source?.proposed_service,
      ["id", "proposed_service_id"],
      ["name", "proposed_service"],
    ),
    next_action: normalizeMasterList(
      source?.next_action,
      ["id", "next_action_id"],
      ["name", "next_action"],
    ),
    sales_status: normalizeMasterList(
      source?.sales_status,
      ["id", "sales_status_id"],
      ["name", "sales_status"],
    ),
    lost_reason: normalizeMasterList(
      source?.lost_reason ??
        source?.lost_reason_list ??
        source?.master_lost_reason,
      ["id", "lost_reason_id"],
      ["name", "lost_reason_name"],
    ),
    lead_sources: normalizeMasterList(
      source?.lead_sources ?? source?.lead_sources_list,
      ["id", "lead_sources_id"],
      ["name", "lead_sources"],
    ),
  };
};

const normalizeContactPerson = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return "";
  return /様$/.test(normalized) ? normalized : `${normalized} 様`;
};

const LOST_SALES_STATUS_ID = 7;

const isLostStatusItem = (item: MasterItem | undefined) => {
  if (!item) return false;

  const name = item.name.normalize("NFKC");

  return (
    item.id === LOST_SALES_STATUS_ID ||
    name.includes("失注") ||
    name.includes("見送り") ||
    name.includes("停止")
  );
};

// =========================================
// ラジオボタン共通部品
// 選択肢が少なく、増減が少ない項目向け
// =========================================

type RadioMasterFieldProps = {
  label: string;
  value: number | "";
  options: MasterItem[];
  onChange: (value: number) => void;
  accentColor?: string;
  required?: boolean;
  helperText?: string;
};

const RadioMasterField = ({
  label,
  value,
  options,
  onChange,
  accentColor = designTokens.brand.primary,
  required = false,
  helperText,
}: RadioMasterFieldProps) => {
  return (
    <FormControl
      fullWidth
      required={required}
      sx={{
        borderRadius: 2,
        backgroundColor: designTokens.surface.subtle,
        border: `1px solid ${designTokens.border.default}`,
        px: 1.5,
        py: 1.25,
        transition: "border-color .18s ease, box-shadow .18s ease",
        "&:hover": {
          borderColor: designTokens.border.strong,
        },
        "&:focus-within": {
          borderColor: accentColor,
          boxShadow: `0 0 0 2px ${accentColor}22`,
        },
      }}
    >
      <FormLabel
        sx={{
          color: "text.secondary",
          fontSize: "0.86rem",
          fontWeight: 700,
          mb: 1,
          "&.Mui-focused": {
            color: accentColor,
          },
          "& .MuiFormLabel-asterisk": {
            color: "error.main",
          },
        }}
      >
        {label}
      </FormLabel>

      <RadioGroup
        value={value === "" ? "" : String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(130px, 1fr))",
          },
          gap: 1,
        }}
      >
        {options.map((item) => {
          const checked = value !== "" && Number(value) === item.id;

          return (
            <FormControlLabel
              key={item.id}
              value={String(item.id)}
              control={
                <Radio
                  size="small"
                  sx={{
                    color: designTokens.text.muted,
                    p: 0.75,
                    "&.Mui-checked": {
                      color: accentColor,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: checked
                      ? designTokens.text.primary
                      : designTokens.text.secondary,
                    fontSize: "0.9rem",
                    fontWeight: checked ? 800 : 600,
                    lineHeight: 1.25,
                  }}
                >
                  {item.name}
                </Typography>
              }
              sx={{
                m: 0,
                px: 1,
                py: 0.8,
                borderRadius: 2,
                border: checked
                  ? `1px solid ${accentColor}`
                  : `1px solid ${designTokens.border.default}`,
                backgroundColor: checked
                  ? `${accentColor}1A`
                  : designTokens.surface.paper,
                transition:
                  "border-color .18s ease, background-color .18s ease, transform .18s ease",
                "&:hover": {
                  borderColor: accentColor,
                  backgroundColor: `${accentColor}14`,
                  transform: "translateY(-1px)",
                },
              }}
            />
          );
        })}
      </RadioGroup>

      {helperText && (
        <FormHelperText
          sx={{
            color: "text.secondary",
            mx: 0,
            mt: 0.9,
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// =========================================
// コンポーネント
// =========================================

export type SalesReportCustomerType = "prospective" | "existing";

const SalesReport = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const projectParams = new URLSearchParams(location.search);
  const lockedProjectId = Number(projectParams.get("projectId"));
  const lockedCompanyId = Number(projectParams.get("companyId"));
  const requestedCustomerType =
    projectParams.get("customerType") ?? projectParams.get("customer_type");
  const projectCreateMode =
    Number.isFinite(lockedProjectId) && lockedProjectId > 0;
  const [customerType, setCustomerType] = useState<SalesReportCustomerType>(
    requestedCustomerType === "existing" ? "existing" : "prospective",
  );

  const isProspective = customerType === "prospective";
  const customerTypeLabel = isProspective ? "見込み顧客" : "既存顧客";
  const today = new Date().toISOString().slice(0, 10);

  const [listData, setListData] = useState<SalesHistoryRow[]>([]);
  const [masters, setMasters] = useState<Masters>(emptyMasters);
  const [formData, setFormData] = useState<SalesHistoryForm>(emptyFormData);
  const [selectedProjects, setSelectedProjects] = useState<
    Array<ProjectMasterItem | string>
  >([]);
  const [projectInputValue, setProjectInputValue] = useState("");
  const [newProjectSummary, setNewProjectSummary] = useState("");
  const [projectStartReason, setProjectStartReason] = useState("");
  const hasExistingProject = selectedProjects.some(
    (project) => typeof project !== "string",
  );
  const hasProjectValue =
    selectedProjects.length > 0 || projectInputValue.trim() !== "";
  const showLeadSourceFields = isProspective && !hasExistingProject;
  const selectedProjectStartReason = selectedProjects
    .filter((project): project is ProjectMasterItem => typeof project !== "string")
    .map((project) => project.project_start_reason?.trim() ?? "")
    .filter(Boolean)
    .join("、");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreview, setAttachmentPreview] = useState<{
    name: string;
    url: string;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: SnackbarSeverity = "success",
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const selectedSalesStatus = masters.sales_status.find(
    (item) => item.id === formData.sales_status_id,
  );
  const isLostStatusSelected = isLostStatusItem(selectedSalesStatus);

  const selectedLostReason = masters.lost_reason.find(
    (item) => item.id === formData.lost_reason_id,
  );
  const recyclePriority = selectedLostReason?.recycle_base_priority ?? "";

  // =========================================
  // 一覧 + マスタ再取得
  // =========================================

  const refreshList = async () => {
    const refreshRes = await axios.post(
      ConstList.getApiUrl() + "ProspectiveSalesReport.php",
      {
        flag: "Pros_Sales_Report",
        customer_type: customerType,
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      },
      ConstList.AXIOS_CONFIG,
    );

    const data = refreshRes.data?.data;

    if (data) {
      setListData(Array.isArray(data.list) ? data.list : []);
      setMasters(normalizeMasters(data.masters));
    } else {
      setListData([]);
      setMasters(emptyMasters);
    }
  };

  // =========================================
  // 初期データ取得
  // =========================================

  useEffect(() => {
    if (!loginUserData.accessToken) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await axios.post(
          ConstList.getApiUrl() + "ProspectiveSalesReport.php",
          {
            flag: "Pros_Sales_Report",
            customer_type: customerType,
            access_token: loginUserData.accessToken,
            algorithm: loginUserData.algorithm,
          },
          ConstList.AXIOS_CONFIG,
        );

        let responseBody = res.data;
        if (typeof responseBody === "string") {
          try {
            responseBody = JSON.parse(responseBody);
          } catch {
            throw new Error("日報APIがJSON以外のレスポンスを返しました。");
          }
        }
        const data = responseBody?.data ?? responseBody;

        if (
          !data ||
          data === false ||
          typeof data !== "object" ||
          !data.masters
        ) {
          throw new Error(
            responseBody?.message ??
              `データ取得に失敗しました（HTTP ${res.status}）。`,
          );
        }

        setListData(Array.isArray(data.list) ? data.list : []);
        const normalizedMasters = normalizeMasters(data.masters);
        setMasters(normalizedMasters);
        const company = normalizedMasters.company.find(
          (item) => item.id === lockedCompanyId,
        );
        if (company) {
          setFormData((current) => ({
            ...current,
            company_id: company.id,
            company_name: company.name,
          }));
        }
        if (projectCreateMode) {
          const project = normalizedMasters.project.find(
            (item) =>
              item.id === lockedProjectId &&
              item.company_id === lockedCompanyId,
          );
          if (company && project) {
            setFormData((current) => ({
              ...current,
              company_id: company.id,
              company_name: company.name,
            }));
            setSelectedProjects([project]);
          }
        }
      } catch (err) {
        console.error("取得エラー", err);
        setErrorMessage("データ取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    loginUserData.accessToken,
    loginUserData.algorithm,
    customerType,
    projectCreateMode,
    lockedCompanyId,
    lockedProjectId,
  ]);

  // =========================================
  // 保存
  // =========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingItems: string[] = [];
    const pendingProjectName = projectInputValue.trim();
    const projectsToSubmit =
      selectedProjects.length > 0
        ? selectedProjects
        : pendingProjectName
          ? [pendingProjectName]
          : [];

    if (!formData.company_id || !formData.company_name.trim()) {
      missingItems.push("会社名");
    }

    if (!hasProjectValue) {
      missingItems.push("案件名");
    }

    if (!formData.contact_person.trim()) {
      missingItems.push("顧客窓口");
    }

    if (showLeadSourceFields && !projectStartReason.trim()) {
      missingItems.push("流入経路");
    }

    if (!formData.sales_person_id) {
      missingItems.push("営業担当者");
    }

    if (!formData.sales_date) {
      missingItems.push("アプローチ日");
    }

    if (formData.sales_date > today) {
      showSnackbar("実施日・対応日に未来の日付は指定できません", "warning");
      return;
    }

    if (!formData.sales_approach_method_id) {
      missingItems.push("営業方法");
    }

    if (isProspective && !formData.purchase_intention_id) {
      missingItems.push("購買意欲");
    }

    if (isProspective && !formData.proposed_service_id) {
      missingItems.push("提案サービス");
    }

    if (!formData.proposal_details.trim()) {
      missingItems.push(isProspective ? "提案内容" : "対応内容");
    }

    if (isProspective && !formData.customer_needs.trim()) {
      missingItems.push("課題・ニーズ");
    }

    if (isProspective && !formData.next_action_id) {
      missingItems.push("次回アクション");
    }

    if (isProspective && !formData.follow_up_deadline) {
      missingItems.push("次回予定");
    }

    if (formData.follow_up_deadline && formData.follow_up_deadline < today) {
      showSnackbar("次回予定は今日以降の日付を指定してください", "warning");
      return;
    }

    if (formData.response_deadline && formData.response_deadline < today) {
      showSnackbar("対応期限は今日以降の日付を指定してください", "warning");
      return;
    }

    if (!isProspective && !formData.request_inquiry.trim()) {
      missingItems.push("要望・問い合わせ");
    }

    if (isProspective && !formData.sales_status_id) {
      missingItems.push("営業後の進捗状況");
    }

    if (isProspective && isLostStatusSelected && !formData.lost_reason_id) {
      missingItems.push("失注・停止理由");
    }

    if (missingItems.length > 0) {
      showSnackbar(`${missingItems.join("、")}を入力してください`, "warning");
      return;
    }

    if (formData.man_hours !== "" && !/^[0-9]+$/.test(formData.man_hours)) {
      showSnackbar("対応にかかった時間は数字のみ入力してください", "warning");
      return;
    }

    try {
      setIsSaving(true);

      const requestData = {
        flag: "Insert_Pros_Sales_Report",
        customer_type: customerType,
        customer_type_id: isProspective ? 1 : 2,
        ...formData,
        project_ids: projectsToSubmit
          .filter(
            (project): project is ProjectMasterItem =>
              typeof project !== "string",
          )
          .map((project) => project.id),
        new_project_names: projectsToSubmit
          .filter((project): project is string => typeof project === "string")
          .map((project) => project.trim())
          .filter(Boolean),
        project_summary: newProjectSummary.trim(),
        project_start_reason: showLeadSourceFields
          ? projectStartReason.trim()
          : "",

        // 登録時に不要な空白を取り除く
        company_name: formData.company_name.trim(),
        contact_person: normalizeContactPerson(formData.contact_person),
        proposal_details: formData.proposal_details.trim(),
        sales_review: formData.sales_review.trim(),
        lead_sources_id: showLeadSourceFields ? formData.lead_sources_id : "",
        referrer: isProspective ? formData.referrer.trim() : "",
        customer_needs: isProspective ? formData.customer_needs.trim() : "",
        competitor_information: isProspective
          ? formData.competitor_information.trim()
          : "",
        next_schedule: isProspective
          ? formData.follow_up_deadline
          : formData.response_deadline,
        follow_up_deadline: isProspective ? formData.follow_up_deadline : "",
        request_inquiry: isProspective ? "" : formData.request_inquiry.trim(),
        issue_complaint: isProspective ? "" : formData.issue_complaint.trim(),
        next_response_details: isProspective
          ? ""
          : formData.next_response_details.trim(),
        response_deadline: isProspective ? "" : formData.response_deadline,
        lost_reason_id: isLostStatusSelected ? formData.lost_reason_id : null,

        // 登録時だけ数値に変換
        man_hours: formData.man_hours === "" ? 0 : Number(formData.man_hours),

        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      };

      const multipartData = new FormData();
      Object.entries(requestData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          multipartData.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined) {
          multipartData.append(key, String(value));
        }
      });
      attachments.forEach((file) =>
        multipartData.append("attachments[]", file),
      );

      const res = await axios.post(
        ConstList.getApiUrl() + "ProspectiveSalesReport.php",
        multipartData,
        {
          ...ConstList.AXIOS_CONFIG,
          // FormDataのContent-Typeとboundaryはブラウザに設定させる
          headers: {},
        },
      );

      console.log("登録結果:", res.data);

      let responseBody = res.data;
      if (typeof responseBody === "string") {
        try {
          responseBody = JSON.parse(responseBody);
        } catch {
          throw new Error("登録APIのレスポンスを解析できませんでした。");
        }
      }

      if (responseBody?.data === false || responseBody?.status === 0) {
        showSnackbar(res.data?.message ?? "登録に失敗しました", "error");
        return;
      }

      await refreshList();
      const returnTo = location.state?.returnTo;
      if (typeof returnTo === "string" && returnTo) {
        navigate(returnTo, { replace: true });
        return;
      }

      // 成功した時だけフォームを初期化
      setFormData(emptyFormData);
      setSelectedProjects([]);
      setProjectStartReason("");
      setAttachments([]);

      showSnackbar(
        isProspective
          ? "見込み顧客の日報を登録しました。"
          : "既存顧客の日報を登録しました。",
        "success",
      );
    } catch (err) {
      console.error(err);
      showSnackbar("登録に失敗しました", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // =========================================
  // キャンセル
  // =========================================

  const handleCancel = () => {
    if (!window.confirm("入力内容を破棄しますか？")) return;
    setFormData(emptyFormData);
    setSelectedProjects([]);
    setAttachments([]);
  };

  const handleCalendarExport = () => {
    const deadline = (
      isProspective ? formData.follow_up_deadline : formData.response_deadline
    ).trim();
    if (!deadline) {
      showSnackbar("対応日を選択してください", "warning");
      return;
    }

    const projectName = selectedProjects
      .map((project) =>
        typeof project === "string" ? project : project.name,
      )
      .filter(Boolean)
      .join("、") || projectInputValue.trim() || "案件未指定";
    const nextAction = isProspective
      ? masters.next_action.find((item) => item.id === formData.next_action_id)
          ?.name ?? ""
      : formData.next_response_details.trim();
    const companyName = formData.company_name.trim() || "会社未指定";
    const details = [
      `会社名：${companyName}`,
      `案件名：${projectName}`,
      `次回対応：${nextAction || "未入力"}`,
    ].join("\n");
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}Z$/, "Z");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sales Data Management System//JP",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:sales-report-${Date.now()}@sales-data-management-system`,
      `DTSTAMP:${timestamp}`,
      `DTSTART;VALUE=DATE:${toIcsDate(deadline)}`,
      `DTEND;VALUE=DATE:${toNextIcsDate(deadline)}`,
      `SUMMARY:${escapeIcsText(`${companyName} / ${projectName} 次回対応`)}`,
      `DESCRIPTION:${escapeIcsText(details)}`,
      "END:VEVENT",
      "END:VCALENDAR",
      "",
    ].join("\r\n");

    const blob = new Blob([`\uFEFF${ics}`], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `次回対応_${deadline}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showSnackbar("カレンダー登録用ファイルをダウンロードしました", "success");
  };

  const handleAttachmentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";

    const officeFile = selectedFiles.find((file) => {
      const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
      return [".ppt", ".pptx", ".xls", ".xlsx"].includes(extension);
    });
    if (officeFile) {
      showSnackbar(
        "Excel・PowerPointは添付できません。PDFに変換してから添付してください。",
        "error",
      );
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) => !isSupportedAttachment(file) || file.size > ATTACHMENT_MAX_SIZE,
    );
    if (invalidFile) {
      showSnackbar(
        `${invalidFile.name} は添付できません。PowerPoint・Excel・PDF、20MB以内のファイルを選択してください。`,
        "warning",
      );
      return;
    }

    setAttachments((current) => {
      const merged = [...current, ...selectedFiles];
      return merged.filter(
        (file, index, files) =>
          files.findIndex(
            (candidate) =>
              candidate.name === file.name &&
              candidate.size === file.size &&
              candidate.lastModified === file.lastModified,
          ) === index,
      );
    });
  };

  const removeAttachment = (indexToRemove: number) => {
    setAttachments((current) =>
      current.filter((_, index) => index !== indexToRemove),
    );
  };

  const openAttachment = (file: File) => {
    const objectUrl = URL.createObjectURL(file);
    setAttachmentPreview({ name: file.name, url: objectUrl });
  };

  const handleCustomerTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    nextCustomerType: SalesReportCustomerType | null,
  ) => {
    if (!nextCustomerType || nextCustomerType === customerType) return;

    setCustomerType(nextCustomerType);
    setFormData((current) => ({
      ...current,
      purchase_intention_id:
        nextCustomerType === "prospective" ? current.purchase_intention_id : "",
      proposed_service_id:
        nextCustomerType === "prospective" ? current.proposed_service_id : "",
      sales_status_id:
        nextCustomerType === "prospective" ? current.sales_status_id : "",
      lost_reason_id:
        nextCustomerType === "prospective" ? current.lost_reason_id : "",
      lead_sources_id:
        nextCustomerType === "prospective" ? current.lead_sources_id : "",
      referrer: nextCustomerType === "prospective" ? current.referrer : "",
      customer_needs:
        nextCustomerType === "prospective" ? current.customer_needs : "",
      competitor_information:
        nextCustomerType === "prospective"
          ? current.competitor_information
          : "",
      follow_up_deadline:
        nextCustomerType === "prospective" ? current.follow_up_deadline : "",
      request_inquiry:
        nextCustomerType === "existing" ? current.request_inquiry : "",
      issue_complaint:
        nextCustomerType === "existing" ? current.issue_complaint : "",
      next_response_details:
        nextCustomerType === "existing" ? current.next_response_details : "",
      response_deadline:
        nextCustomerType === "existing" ? current.response_deadline : "",
    }));
  };

  // =========================================
  // 入力欄デザイン
  // =========================================

  const writableInputPropsSx = {
    backgroundColor: designTokens.surface.paper,
    color: designTokens.text.primary,

    "& .MuiOutlinedInput-input": {
      color: designTokens.text.primary,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.border.strong,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.text.muted,
    },

    "&.Mui-focused": {
      backgroundColor: designTokens.surface.paper,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.brand.dark,
    },
  };

  const writableLabelSx = {
    "& .MuiInputLabel-root": {
      color: designTokens.text.secondary,
      fontSize: "0.9rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: designTokens.text.primary,
      fontSize: "1.05rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },

    "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
      color: designTokens.brand.dark,
    },

    "& .MuiSelect-icon": {
      color: designTokens.brand.dark,
    },
  };

  const darkFixedInputSx = {
    backgroundColor: designTokens.surface.subtle,
    color: designTokens.text.secondary,

    "& .MuiOutlinedInput-input": {
      color: designTokens.text.secondary,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.border.strong,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.border.strong,
    },

    "&.Mui-focused": {
      backgroundColor: designTokens.surface.subtle,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.75)",
    },

    "&.Mui-focused .MuiOutlinedInput-input": {
      color: designTokens.text.secondary,
    },

    "& .MuiSelect-icon": {
      color: designTokens.brand.dark,
    },

    "& .MuiAutocomplete-popupIndicator": {
      color: designTokens.brand.dark,
    },

    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter:
        "invert(85%) sepia(45%) saturate(600%) hue-rotate(20deg) brightness(100%) contrast(95%)",
      opacity: 0.95,
      cursor: "pointer",
    },
  };

  const reflectionInputPropsSx = {
    backgroundColor: designTokens.surface.paper,
    color: designTokens.text.primary,

    "& .MuiOutlinedInput-input": {
      color: designTokens.text.primary,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.border.strong,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.text.muted,
    },

    "&.Mui-focused": {
      backgroundColor: designTokens.surface.paper,
      boxShadow: "0 0 0 3px rgba(152,188,46,0.12)",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: designTokens.text.muted,
      borderWidth: 1,
    },
  };

  // =========================================
  // 画面全体の共通UI
  // =========================================

  const reportPageSx = {
    backgroundColor: designTokens.surface.page,
    border: `1px solid ${designTokens.border.default}`,
    borderRadius: 1.5,
    px: { xs: 2, sm: 3 },
  };

  const reportCardSx = {
    mb: 3,
    backgroundColor: designTokens.surface.subtle,
    border: `1px solid ${designTokens.border.default}`,
    borderRadius: 1.5,
    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
  };

  const reportAccentCardSx = {
    ...reportCardSx,
    backgroundColor: designTokens.surface.paper,
    border: `1px solid ${designTokens.overlay.brandMedium}`,
  };

  const reportMemoCardSx = {
    ...reportCardSx,
    backgroundColor: designTokens.surface.subtle,
    border: `1px solid ${designTokens.border.default}`,
  };

  const cardContentSx = {
    p: { xs: 2, sm: 3 },
  };

  const SectionHeader = ({
    title,
    description,
    accent = false,
    darkText = false,
  }: {
    title: string;
    description?: string;
    accent?: boolean;
    darkText?: boolean;
  }) => (
    <Box mb={2.5}>
      <Typography
        fontWeight={700}
        sx={{
          color: accent ? designTokens.brand.dark : designTokens.text.primary,
          fontSize: "1rem",
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          sx={{
            color: designTokens.text.secondary,
            mt: 0.5,
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  // 営業日報登録画面では選ばせない進捗状況
  // 1 = 未アプローチ / 未着手 の想定
  const selectableSalesStatusList = masters.sales_status.filter(
    (item) =>
      item.id !== 1 && item.name !== "未アプローチ" && item.name !== "未着手",
  );

  return (
    <>
      <form onSubmit={handleSubmit} noValidate>
        <Box maxWidth={900} mx="auto" py={4} sx={reportPageSx}>
          {/* ===== タイトル ===== */}
          <Box mb={3}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color: designTokens.text.primary,
                letterSpacing: "0.04em",
              }}
            >
              {customerTypeLabel} 日報
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.75,
              }}
            ></Typography>
          </Box>

          {errorMessage && (
            <Box mb={2}>
              <Typography color="error">{errorMessage}</Typography>
            </Box>
          )}

          {/* ===== 顧客区分 ===== */}
          <Card sx={reportAccentCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader title="顧客区分" accent />

              <ToggleButtonGroup
                exclusive
                fullWidth
                value={customerType}
                disabled={projectCreateMode}
                onChange={handleCustomerTypeChange}
                aria-label="日報を登録する顧客区分"
                sx={{
                  gap: 1.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    m: 0,
                    border: `1px solid ${designTokens.border.strong}`,
                    borderRadius: "12px !important",
                  },
                }}
              >
                {(
                  [
                    ["prospective", "見込み顧客", "新規商談・受注前"],
                    ["existing", "既存顧客", "契約済み・既存取引先"],
                  ] as const
                ).map(([value, label, description]) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      py: 0.75,
                      minHeight: 52,
                      color: designTokens.text.secondary,
                      fontWeight: 800,
                      "&.Mui-selected": {
                        color: designTokens.brand.dark,
                        backgroundColor: designTokens.overlay.brandSoft,
                        borderColor: designTokens.brand.primary,
                      },
                    }}
                  >
                    <Box sx={{ textAlign: "left" }}>
                      <Typography fontWeight={800} fontSize={14}>
                        {label}
                      </Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {description}
                      </Typography>
                    </Box>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </CardContent>
          </Card>

          {/* ===== 基本情報 ===== */}
          <Card sx={reportCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="基本情報"
                description={
                  isProspective
                    ? "営業活動の基本情報を入力します。"
                    : "顧客対応の基本情報を入力します。"
                }
              />

              <Stack spacing={2.5}>
                <Autocomplete
                  fullWidth
                  options={masters.company}
                  disabled={projectCreateMode}
                  getOptionLabel={(option) => option.name ?? ""}
                  value={
                    masters.company.find(
                      (row) => row.id === formData.company_id,
                    ) ?? null
                  }
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      company_id: newValue?.id ?? "",
                      company_name: newValue?.name ?? "",
                    });
                    setSelectedProjects([]);
                    setProjectInputValue("");
                    setNewProjectSummary("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="会社名"
                      required
                      variant="outlined"
                      size="small"
                      sx={darkFixedInputSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{ color: designTokens.text.muted }}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  freeSolo
                  fullWidth
                  disabled={!formData.company_id || projectCreateMode}
                  options={masters.project.filter(
                    (project) =>
                      project.company_id === formData.company_id &&
                      project.customer_type_id === (isProspective ? 1 : 2) &&
                      project.status === "active",
                  )}
                  value={selectedProjects}
                  inputValue={projectInputValue}
                  onInputChange={(_, value) => {
                    if (!isProspective && selectedProjects.length > 0) return;
                    setProjectInputValue(value);
                  }}
                  onChange={(_, values) => {
                    const normalizedValues = values.reduce<
                      Array<ProjectMasterItem | string>
                    >((result, value) => {
                      if (typeof value !== "string") {
                        if (
                          !result.some(
                            (item) =>
                              typeof item !== "string" && item.id === value.id,
                          )
                        ) {
                          result.push(value);
                        }
                        return result;
                      }

                      const name = value.trim();
                      if (!name) return result;

                      const existingProject = masters.project.find(
                        (project) =>
                          project.company_id === formData.company_id &&
                          project.name.normalize("NFKC").toLocaleLowerCase() ===
                            name.normalize("NFKC").toLocaleLowerCase(),
                      );

                      if (existingProject) {
                        if (
                          !result.some(
                            (item) =>
                              typeof item !== "string" &&
                              item.id === existingProject.id,
                          )
                        ) {
                          result.push(existingProject);
                        }
                      } else if (
                        !result.some(
                          (item) =>
                            typeof item === "string" &&
                            item.normalize("NFKC").toLocaleLowerCase() ===
                              name.normalize("NFKC").toLocaleLowerCase(),
                        )
                      ) {
                        result.push(name);
                      }

                      return result;
                    }, []);

                    setSelectedProjects(normalizedValues);
                    setProjectInputValue("");
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  isOptionEqualToValue={(option, value) =>
                    typeof option !== "string" &&
                    typeof value !== "string" &&
                    option.id === value.id
                  }
                  renderTags={(values, getTagProps) =>
                    values.map((value, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={
                          typeof value === "string" ? `new-${value}` : value.id
                        }
                        label={
                          typeof value === "string"
                            ? `${value}（新規）`
                            : value.name
                        }
                        size="small"
                        color={
                          typeof value === "string" ? "primary" : "default"
                        }
                        variant="outlined"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="案件名"
                      placeholder={
                        formData.company_id
                          ? "案件を選択、または新しい案件名を入力してEnter"
                          : "先に会社名を選択してください"
                      }
                      helperText="複数選択できます。新しい案件名は日報登録時に案件マスターへ登録されます。"
                      sx={darkFixedInputSx}
                    />
                  )}
                />

                {(selectedProjects.some(
                  (project) => typeof project === "string",
                ) ||
                  projectInputValue.trim() !== "") && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="案件概要"
                    value={newProjectSummary}
                    onChange={(event) =>
                      setNewProjectSummary(event.target.value)
                    }
                    helperText="新規案件として登録する案件の概要を入力してください。"
                    sx={darkFixedInputSx}
                  />
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="営業担当者"
                    required
                    value={formData.sales_person_id}
                    sx={darkFixedInputSx}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sales_person_id: Number(e.target.value),
                      })
                    }
                  >
                    {masters.sales_person.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    type="date"
                    label={isProspective ? "アプローチ日" : "対応日"}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={formData.sales_date}
                    inputProps={{ max: today }}
                    sx={darkFixedInputSx}
                    onChange={(e) =>
                      setFormData({ ...formData, sales_date: e.target.value })
                    }
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="顧客窓口"
                  placeholder="例：〇〇部 △△様"
                  required
                  value={formData.contact_person}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">様</InputAdornment>
                    ),
                  }}
                  sx={darkFixedInputSx}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contact_person: e.target.value,
                    })
                  }
                />

                {hasExistingProject && selectedProjectStartReason && (
                  <TextField
                    fullWidth
                    label="流入経路"
                    value={selectedProjectStartReason}
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="案件に登録されている案件開始理由を自動表示しています。"
                    sx={reflectionInputPropsSx}
                  />
                )}

                {showLeadSourceFields && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="案件開始理由"
                      required
                      multiline
                      minRows={2}
                      value={projectStartReason}
                      onChange={(e) => setProjectStartReason(e.target.value)}
                      sx={darkFixedInputSx}
                    />
                  </Stack>
                )}

                {false && showLeadSourceFields && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      select
                      fullWidth
                      label="流入経路"
                      required
                      value={formData.lead_sources_id}
                      sx={darkFixedInputSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          lead_sources_id: Number(e.target.value),
                        })
                      }
                    >
                      {masters.lead_sources.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      fullWidth
                      label="紹介元"
                      placeholder="例：株式会社〇〇 △△様"
                      value={formData.referrer}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          referrer: e.target.value,
                        })
                      }
                    />
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 商談内容 ===== */}
          <Card sx={reportAccentCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title={isProspective ? "商談内容" : "対応内容"}
                description={
                  isProspective
                    ? "営業方法、購買意欲、提案サービス、提案内容を記録します。"
                    : "対応方法、対応時間、対応内容を記録します。"
                }
                accent
              />

              <Stack spacing={2.5}>
                <TextField
                  select
                  fullWidth
                  label={isProspective ? "営業方法" : "対応方法"}
                  required
                  value={formData.sales_approach_method_id}
                  sx={darkFixedInputSx}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sales_approach_method_id: Number(e.target.value),
                    })
                  }
                >
                  {masters.sales_approach_method.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="対応時間"
                  helperText="分単位で入力してください（例：90分）"
                  value={formData.man_hours}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "" || /^[0-9]+$/.test(value)) {
                      setFormData({
                        ...formData,
                        man_hours: value,
                      });
                    }
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  InputProps={{ sx: writableInputPropsSx }}
                  sx={writableLabelSx}
                  fullWidth
                />

                {isProspective && (
                  <>
                    <RadioMasterField
                      label="購買意欲"
                      required
                      value={formData.purchase_intention_id}
                      options={masters.purchase_intention}
                      accentColor={designTokens.brand.primary}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          purchase_intention_id: value,
                        })
                      }
                    />

                    <RadioMasterField
                      label="提案サービス"
                      required
                      value={formData.proposed_service_id}
                      options={masters.proposed_service}
                      accentColor="#64B5F6"
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          proposed_service_id: value,
                        })
                      }
                    />
                  </>
                )}

                <TextField
                  label={isProspective ? "提案内容" : "対応内容"}
                  multiline
                  rows={4}
                  required
                  value={formData.proposal_details}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      proposal_details: e.target.value,
                    })
                  }
                  InputProps={{ sx: writableInputPropsSx }}
                  sx={writableLabelSx}
                  placeholder={
                    isProspective
                      ? "例：提案した内容、相手の反応、課題として挙がった内容など"
                      : "例：議事録など"
                  }
                  FormHelperTextProps={{
                    sx: {
                      color: "text.secondary",
                      ml: 0,
                    },
                  }}
                />

                {isProspective ? (
                  <>
                    <TextField
                      label="課題・ニーズ"
                      required
                      multiline
                      rows={3}
                      fullWidth
                      value={formData.customer_needs}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      placeholder="顧客が抱えている課題、実現したいこと、優先事項など"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customer_needs: e.target.value,
                        })
                      }
                    />

                    <TextField
                      label="競合情報"
                      multiline
                      rows={2}
                      fullWidth
                      value={formData.competitor_information}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      placeholder="比較中の他社、他社提案の状況、選定条件など"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          competitor_information: e.target.value,
                        })
                      }
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="要望・問い合わせ"
                      required
                      multiline
                      rows={3}
                      fullWidth
                      value={formData.request_inquiry}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      placeholder="顧客から受けた要望や問い合わせ内容"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          request_inquiry: e.target.value,
                        })
                      }
                    />

                    <TextField
                      label="問題・クレーム"
                      multiline
                      rows={3}
                      fullWidth
                      value={formData.issue_complaint}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      placeholder="発生している問題、クレーム、影響範囲や緊急度など"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          issue_complaint: e.target.value,
                        })
                      }
                    />
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 結果・次回対応 ===== */}
          <Card sx={reportCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title={isProspective ? "結果・次回対応" : "次回対応"}
                description={
                  isProspective
                    ? "この営業活動後の顧客状態と、次に行う対応を記録します。"
                    : "今回の対応後に必要となる次回対応を記録します。"
                }
              />

              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {isProspective && (
                    <TextField
                      select
                      fullWidth
                      label="営業後の進捗状況"
                      required
                      value={formData.sales_status_id}
                      sx={darkFixedInputSx}
                      onChange={(e) => {
                        const statusId = Number(e.target.value);
                        const statusItem = masters.sales_status.find(
                          (item) => item.id === statusId,
                        );

                        setFormData({
                          ...formData,
                          sales_status_id: statusId,
                          lost_reason_id: isLostStatusItem(statusItem)
                            ? formData.lost_reason_id
                            : "",
                        });
                      }}
                      helperText="顧客一覧のアプローチ状況に反映されます。"
                      FormHelperTextProps={{
                        sx: {
                          color: "text.secondary",
                          mt: 0.75,
                        },
                      }}
                    >
                      {selectableSalesStatusList.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {isProspective && (
                    <TextField
                      select
                      fullWidth
                      label="次回アクション"
                      required={isProspective}
                      value={formData.next_action_id}
                      sx={darkFixedInputSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          next_action_id: Number(e.target.value),
                        })
                      }
                    >
                      {masters.next_action.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {!isProspective && (
                    <TextField
                      fullWidth
                      label="次回対応"
                      placeholder="例：調査結果を連絡し、修正版を提出"
                      value={formData.next_response_details}
                      InputProps={{ sx: writableInputPropsSx }}
                      sx={writableLabelSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          next_response_details: e.target.value,
                        })
                      }
                    />
                  )}

                  <TextField
                    type="date"
                    label={isProspective ? "次回予定" : "対応期限"}
                    inputProps={{ min: today }}
                    InputLabelProps={{ shrink: true }}
                    value={
                      isProspective
                        ? formData.follow_up_deadline
                        : formData.response_deadline
                    }
                    sx={{ ...darkFixedInputSx, minWidth: { sm: 220 } }}
                    onChange={(e) =>
                      setFormData(
                        isProspective
                          ? {
                              ...formData,
                              follow_up_deadline: e.target.value,
                            }
                          : {
                              ...formData,
                              response_deadline: e.target.value,
                            },
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    disabled={
                      !(isProspective
                        ? formData.follow_up_deadline
                        : formData.response_deadline)
                    }
                    onClick={handleCalendarExport}
                    sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
                  >
                    カレンダーに追加
                  </Button>
                </Stack>

                {isProspective && isLostStatusSelected && (
                  <Box
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      backgroundColor: "rgba(220,90,82,0.08)",
                      border: "1px solid rgba(220,90,82,0.28)",
                    }}
                  >
                    <TextField
                      select
                      fullWidth
                      required
                      label="失注・停止理由"
                      value={formData.lost_reason_id}
                      sx={darkFixedInputSx}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          lost_reason_id: Number(e.target.value),
                        })
                      }
                      helperText={
                        masters.lost_reason.length === 0
                          ? "失注・停止理由マスターが取得できていません。"
                          : "失注・停止扱いの営業履歴として、一覧では行背景の判定に使用されます。"
                      }
                      FormHelperTextProps={{
                        sx: {
                          color: "text.secondary",
                          mt: 0.75,
                        },
                      }}
                    >
                      {masters.lost_reason.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {recyclePriority && (
                      <Typography
                        sx={{
                          mt: 1,
                          color: designTokens.text.primary,
                          fontSize: 12.5,
                          fontWeight: 700,
                        }}
                      >
                        再アプローチ優先度：{recyclePriority}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ===== 振り返りメモ ===== */}
          <Card sx={reportCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader title="添付資料" description="PDF形式にして添付" />

              <Stack spacing={1.5}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  sx={{
                    alignSelf: "flex-start",
                    borderColor: designTokens.border.strong,
                    color: designTokens.text.secondary,
                    borderRadius: 2,
                    fontWeight: 700,
                  }}
                >
                  ファイルを選択
                  <input
                    hidden
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    onChange={handleAttachmentChange}
                  />
                </Button>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "none" }}
                >
                  対応形式：PPT / PPTX / XLS / XLSX / PDF（1ファイル20MB以内）
                </Typography>

                {attachments.length > 0 && (
                  <Stack spacing={1}>
                    {attachments.map((file, index) => (
                      <Box
                        key={`${file.name}-${file.lastModified}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          backgroundColor: designTokens.overlay.brandSoft,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          minWidth={0}
                        >
                          <AttachFileIcon fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            noWrap
                            title={file.name}
                            sx={{ minWidth: 0 }}
                          >
                            {file.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {(file.size / 1024 / 1024).toFixed(1)}MB
                          </Typography>
                        </Stack>
                        <Button
                          type="button"
                          size="small"
                          color="inherit"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => openAttachment(file)}
                          sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
                        >
                          開く
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          color="inherit"
                          aria-label={`${file.name}を削除`}
                          onClick={() => removeAttachment(index)}
                          sx={{ minWidth: 36, p: 0.5 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={reportMemoCardSx}>
            <CardContent sx={cardContentSx}>
              <SectionHeader
                title="振り返りメモ"
                description="次回の営業や社内共有に活かしたい内容を自由に記録します。"
                darkText
              />

              <TextField
                label="振り返りメモ"
                multiline
                rows={3}
                fullWidth
                value={formData.sales_review}
                onChange={(e) =>
                  setFormData({ ...formData, sales_review: e.target.value })
                }
                InputProps={{ sx: reflectionInputPropsSx }}
                sx={writableLabelSx}
                placeholder="例：相手の温度感、次回確認すること、提案時の反省点など"
              />
            </CardContent>
          </Card>

          {/* ===== 操作 ===== */}
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            sx={{
              position: "sticky",
              bottom: 0,
              py: 2,
              background:
                "linear-gradient(180deg, rgba(244,246,248,0.75), rgba(244,246,248,1))",
              borderTop: `1px solid ${designTokens.border.default}`,
              zIndex: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                borderColor: designTokens.border.strong,
                color: designTokens.text.secondary,
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  borderColor: designTokens.brand.primary,
                  backgroundColor: designTokens.overlay.brandSoft,
                },
              }}
            >
              キャンセル
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || loading}
              sx={{
                backgroundColor: designTokens.brand.primary,
                color: designTokens.text.primary,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                "&:hover": {
                  backgroundColor: designTokens.brand.light,
                },
              }}
            >
              {isSaving ? (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              ) : (
                `${customerTypeLabel}日報を記録`
              )}
            </Button>
          </Box>
        </Box>
      </form>

      <Dialog
        open={Boolean(attachmentPreview)}
        onClose={() => {
          if (attachmentPreview) URL.revokeObjectURL(attachmentPreview.url);
          setAttachmentPreview(null);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{attachmentPreview?.name}</DialogTitle>
        <DialogContent
          sx={{ height: "75vh", p: 0, backgroundColor: "#F1F5F9" }}
        >
          {attachmentPreview && (
            <Box
              component="iframe"
              title={attachmentPreview.name}
              src={attachmentPreview.url}
              sx={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (attachmentPreview) URL.revokeObjectURL(attachmentPreview.url);
              setAttachmentPreview(null);
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;

          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }));
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SalesReport;
